/**
 * Insert an image by URL (`061-editor.md` §2 — "From URL"; `031-content-format.md`).
 *
 * Unlike the upload path (`uploads.ts`), the URL is embedded **directly** as the node's `src`:
 * there is no fetch, no `page_asset` row and no `assetId`. The image is therefore invisible to
 * asset GC (which only tracks `assetId`s — harmless), depends on the remote host staying up,
 * and reveals the viewer's IP to that host. This is a deliberate, documented deviation from the
 * "every `src` is a `/uploads/` path" rule; see the spec notes in `031`/`062`.
 *
 * The one hard rule: **only `http(s)` URLs may enter.** `href` is a string, and
 * `javascript:alert(1)` is a perfectly well-formed string (`120` §1.1); a `data:` URL is barred
 * separately by `allowBase64: false` on the image node. The production CSP must also allow the
 * external host — `img-src 'self' blob: https:` in `docker/backend/Caddyfile`; without it the
 * image is blocked in prod while working in dev.
 */
import type { Editor } from '@tiptap/react';

/**
 * Parse a user-entered image URL. Returns the normalised absolute URL if it is a well-formed
 * `http`/`https` URL, or `null` for anything else (empty, unparseable, `javascript:`, `data:`,
 * `file:`, a relative path, …). Pure, so the scheme rule is a unit test, not a bug report.
 */
export function parseImageUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  return url.href;
}

/** Best-effort intrinsic size, so the node reserves the box and text below does not jump. */
async function decodeDimensions(src: string): Promise<{ width: number; height: number } | null> {
  try {
    const image = new Image();
    image.src = src;
    await image.decode();
    if (image.naturalWidth === 0) return null;
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    // A remote host that blocks hotlinking or a broken URL — insert anyway with no dimensions.
    return null;
  }
}

/**
 * Insert an `image` node whose `src` is the external URL. Returns `false` (inserting nothing) if
 * the URL is not an `http(s)` URL. The node is fully resolved from the first frame — no
 * `uploading`/`uploadId`, no pending-upload row, no save gating — so nothing here touches the
 * upload choreography in `uploads.ts`.
 */
export async function insertImageFromUrl(editor: Editor, raw: string): Promise<boolean> {
  const src = parseImageUrl(raw);
  if (src === null) return false;

  const size = await decodeDimensions(src);

  editor
    .chain()
    .focus()
    .insertContent({
      type: 'image',
      attrs: {
        src,
        alt: '',
        assetId: null,
        width: size?.width ?? null,
        height: size?.height ?? null,
      },
    })
    .run();

  return true;
}
